import { GridApi, IRowNode } from 'ag-grid-community'
import { WbsItemLink } from '../types'

// ============================================================================
// Fallback endpoint support (for collapsed group members)
// ============================================================================

export type EndpointInfo = {
  rowIndex: number
  isFallback: boolean
}

export type FallbackLinkInfo<T extends WbsItemLink> = {
  link: T
  from: EndpointInfo
  to: EndpointInfo
}

// Verify that a displayed row at the given index matches the expected projectPlanUuid.
// Uses node.id (which equals the projectPlanUuid / tree row UUID) for O(1) comparison.
const isEndpointVisible = (
  api: GridApi,
  rowIndex: number,
  projectPlanUuid: string
): boolean => {
  if (rowIndex < 0) return false
  const node = api.getDisplayedRowAtIndex(rowIndex)
  return node?.id === projectPlanUuid
}

// Check if the node at ancestorRowIndex is in the parent chain of visiblePlanUuid.
// Used to detect when a resolved ancestor is a common parent of both endpoints,
// in which case the partial line would point "backwards" (e.g. filter case).
const isAncestorOfVisibleEndpoint = (
  api: GridApi,
  ancestorRowIndex: number,
  visiblePlanUuid: string
): boolean => {
  const ancestorNode = api.getDisplayedRowAtIndex(ancestorRowIndex)
  if (!ancestorNode) return false

  const visibleNode = api.getRowNode(visiblePlanUuid)
  if (!visibleNode) return false

  let current: IRowNode | null = visibleNode.parent
  while (current) {
    if (current.id === ancestorNode.id) return true
    current = current.parent
  }
  return false
}

// Find the nearest visible ancestor row for a node identified by projectPlanUuid.
// Uses api.getRowNode() for O(1) lookup, then walks up the parent chain.
export const findVisibleAncestor = (
  api: GridApi,
  projectPlanUuid: string
): number | null => {
  const targetNode = api.getRowNode(projectPlanUuid)
  if (!targetNode) return null

  let current: IRowNode | null = targetNode.parent
  while (current) {
    if (
      current.rowIndex !== null &&
      current.rowIndex !== undefined &&
      current.rowIndex >= 0
    ) {
      return current.rowIndex
    }
    current = current.parent
  }
  return null
}

// Categorize links into three buckets:
// - "visible": both endpoints displayed → normal line
// - "partial": one endpoint hidden but ancestor resolved → dashed terminus line
// - "fallback": one endpoint hidden and ancestor NOT resolved → icon indicator
// Links where both endpoints are hidden are skipped entirely.
export const categorizeLinks = <T extends WbsItemLink>(
  links: T[],
  api: GridApi | undefined
): {
  visible: T[]
  partial: FallbackLinkInfo<T>[]
  fallback: FallbackLinkInfo<T>[]
} => {
  if (!api || links.length === 0) {
    return { visible: [], partial: [], fallback: [] }
  }

  const visible: T[] = []
  const partial: FallbackLinkInfo<T>[] = []
  const fallback: FallbackLinkInfo<T>[] = []

  for (const link of links) {
    const fromVisible = isEndpointVisible(
      api,
      link.fromRowIndex,
      link.fromProjectPlanUuid
    )
    const toVisible = isEndpointVisible(
      api,
      link.toRowIndex,
      link.toProjectPlanUuid
    )

    if (fromVisible && toVisible) {
      visible.push(link)
      continue
    }

    // Both hidden → skip (no line, no icon)
    if (!fromVisible && !toVisible) continue

    // One side hidden → resolve ancestor
    const fromInfo: EndpointInfo = fromVisible
      ? { rowIndex: link.fromRowIndex, isFallback: false }
      : {
          rowIndex: findVisibleAncestor(api, link.fromProjectPlanUuid) ?? -1,
          isFallback: true,
        }
    const toInfo: EndpointInfo = toVisible
      ? { rowIndex: link.toRowIndex, isFallback: false }
      : {
          rowIndex: findVisibleAncestor(api, link.toProjectPlanUuid) ?? -1,
          isFallback: true,
        }

    // Skip only if neither side has a valid position.
    if (fromInfo.rowIndex < 0 && toInfo.rowIndex < 0) continue

    // Ancestor resolved → check if it's a meaningful partial line target.
    // If the ancestor is also a parent of the visible endpoint (common ancestor),
    // the partial line would point "backwards" toward a shared parent rather than
    // toward the hidden item (e.g. when rows are hidden by filtering).
    // In that case, show a fallback icon instead.
    const fallbackSideResolved = fromInfo.isFallback
      ? fromInfo.rowIndex >= 0
      : toInfo.rowIndex >= 0

    if (fallbackSideResolved) {
      const ancestorRowIndex = fromInfo.isFallback
        ? fromInfo.rowIndex
        : toInfo.rowIndex
      const visiblePlanUuid = fromInfo.isFallback
        ? link.toProjectPlanUuid
        : link.fromProjectPlanUuid

      if (isAncestorOfVisibleEndpoint(api, ancestorRowIndex, visiblePlanUuid)) {
        fallback.push({ link, from: fromInfo, to: toInfo })
      } else {
        partial.push({ link, from: fromInfo, to: toInfo })
      }
    } else {
      fallback.push({ link, from: fromInfo, to: toInfo })
    }
  }

  return { visible, partial, fallback }
}
